import * as React from 'react';
import { useIntl } from 'react-intl';
import CmdBox from '@commander-services/cmd-box';
import EcoDriveChart from '../EcoDriveChart';
import DynamicTable from '../../../DynamicTable';
import { TABLE_ECO_DRIVE } from '../../../../Services/TableService';
import Header from './Header';
import RideJustStarted from '../../RideJustStarted';
import { IGraphResponseData } from '../../GraphService';
import PositionDetails from '../../PositionsDetails';

interface IProps {
  rideId: string | null;
  vehicleId: number;
  responseData: IGraphResponseData[];
  isCompressed: boolean;
  children?: JSX.Element;
  loadData: () => void;
  isDetail: boolean;
  isSubpage?: boolean;
  isSelectable: boolean;
  isGdprAlert?: boolean;
}

function Stopped(props: IProps) {
  const { formatMessage: f } = useIntl();
  const [graphData, setGraphData] = React.useState<any>({ xLabels: [], yLabels: [], tooltips: {} });

  const getData = () => {
    const xLabels: string[] = [];
    const yLabels: (number | null)[] = [];
    const minLabels: string[] = [];
    const maxLabels: string[] = [];
    const tooltips = {};
    if (props.responseData) {
      props.responseData.forEach(
        (i: {
          timestamp: string;
          value: number;
          min: string;
          max: string;
          violations: any;
          address: string;
          latitude: string;
          longitude: string;
          distance?: string;
          code: string;
        }) => {
          if (!xLabels.includes(i.timestamp)) {
            xLabels.push(i.timestamp);
            yLabels.push(i.value);
            minLabels.push(i.min);
            maxLabels.push(i.max);
            tooltips[i.timestamp] = {
              address: i.address || `${i.latitude}, ${i.longitude}`,
              violations: i.violations,
              distance: i.distance,
              code: i.code,
            };
          } else if (
            new Date(xLabels[xLabels.length - 1]) === new Date(i.timestamp) &&
            i.violations &&
            i.violations.length > 0
          ) {
            xLabels[xLabels.length - 1] = i.timestamp;
            yLabels[xLabels.length - 1] = i.value;
            tooltips[i.timestamp] = {
              address: i.address || `${i.latitude}, ${i.longitude}`,
              violations: i.violations,
              distance: i.distance,
              code: i.code,
            };
          }
        }
      );
    }
    setGraphData({
      xLabels,
      yLabels,
      minLabels,
      maxLabels,
      tooltips,
    });
  };

  React.useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.responseData]);

  return (
    <div>
      {!props.isSubpage && (
        <RideJustStarted vehicleId={props.vehicleId} loadData={props.loadData} />
      )}
      {props.isDetail ? (
        <div data-cy="reports-form-component">
          <Header
            vehicleId={props.vehicleId}
            rideId={props.rideId || undefined}
            isGdprAlert={props.isGdprAlert}
          />
          <CmdBox id="tripsOverview.section.graph" title={f({ id: 'tripsOverview.section.graph' })}>
            {props.children}
            <div style={{ marginTop: '10px', padding: '20px 0px 20px 25px' }}>
              <EcoDriveChart
                graphData={graphData}
                noAnimation="animation"
                isCompressed={props.isCompressed}
                isDetail={props.isDetail}
                isSelectable={props.isSelectable}
              />
            </div>
          </CmdBox>
          <PositionDetails vehicleId={props.vehicleId} positions={props.responseData} />
          <div style={{ marginTop: '-12px' }}>
            <CmdBox id="violationsList" title={f({ id: 'tripsOverview.section.violationsList' })}>
              <DynamicTable
                name={TABLE_ECO_DRIVE}
                noTab={true}
                mustClosed={true}
                filter={{
                  rideId: props.rideId,
                  vehicleId: props.vehicleId,
                }}
                reloadTable={!!props.rideId}
                noBorder={true}
                dateFormat="DD.MM.YYYY HH:mm:ss"
              />
            </CmdBox>
          </div>
        </div>
      ) : (
        <EcoDriveChart
          graphData={graphData}
          noAnimation="animation"
          isCompressed={props.isCompressed}
          isDetail={props.isDetail}
        />
      )}
    </div>
  );
}

export default React.memo(Stopped);
